import { readableForeground } from './color.utils';
import {
  AppliedTenantTheme,
  CONTENT_PAIRS,
  PLATFORM_PALETTE,
  SURFACE_KEYS,
  TenantTheme,
  TenantThemeVars,
  THEME_COLOR_KEYS,
  ThemeColorKey,
  ThemeMode,
  ThemePalette
} from './tenant-theme.types';

const VAR_BY_KEY: Record<ThemeColorKey, string> = {
  ...(Object.fromEntries(THEME_COLOR_KEYS.map((key) => [key, `--tenant-${key}`])) as Record<ThemeColorKey, string>),
  primary: '--brand',
  'primary-content': '--brand-foreground',
  sidebar: '--sidebar-bg'
};

export const TENANT_THEME_VARS: string[] = [
  ...Object.values(VAR_BY_KEY),
  '--sidebar-bg-foreground',
  '--tenant-muted-foreground',
  '--tenant-radius',
  '--tenant-radius-box',
  '--tenant-radius-field',
  '--tenant-radius-selector'
];

export function hasCustomSurface(vars: TenantThemeVars): boolean {
  return Object.keys(vars).some((name) => name.startsWith('--tenant-base-') || name === '--tenant-border');
}

export function tenantPalette(theme: TenantTheme, mode: ThemeMode): ThemePalette {
  let palette: ThemePalette = { ...theme.light };
  if (mode === 'dark') {
    const own = theme.dark ?? {};
    const inherited: ThemePalette = {};
    for (const key of THEME_COLOR_KEYS) {
      if (!SURFACE_KEYS.includes(key) && theme.light[key]) inherited[key] = theme.light[key];
    }
    for (const [bg, content] of CONTENT_PAIRS) {
      if (own[bg] && !own[content]) delete inherited[content];
    }
    palette = { ...inherited, ...own };
  }

  for (const [bg, content] of CONTENT_PAIRS) {
    const background = palette[bg];
    if (background && !palette[content]) {
      palette[content] = bg === 'base-100' ? readableForeground(background, '#1f2937', '#f2f2f2') : readableForeground(background);
    }
  }
  return palette;
}

export function effectivePalette(theme: TenantTheme, mode: ThemeMode): Record<ThemeColorKey, string> {
  const own = tenantPalette(theme, mode);
  const palette = { ...PLATFORM_PALETTE[mode], ...own };
  if (!own.accent) palette.accent = palette.primary;
  if (!own['accent-content']) palette['accent-content'] = palette['primary-content'];
  if (!own.sidebar) palette.sidebar = palette['base-100'];
  return palette;
}

export function resolveTenantTheme(theme: TenantTheme): AppliedTenantTheme {
  const build = (mode: ThemeMode): TenantThemeVars => {
    const palette = tenantPalette(theme, mode);
    const vars: TenantThemeVars = {};
    for (const key of THEME_COLOR_KEYS) {
      const value = palette[key];
      if (value) vars[VAR_BY_KEY[key]] = value;
    }
    if (palette.sidebar) {
      vars['--sidebar-bg-foreground'] = readableForeground(palette.sidebar, '#000000', '#f2f2f2');
    }
    if (palette['base-content']) {
      vars['--tenant-muted-foreground'] = `color-mix(in oklch, ${palette['base-content']} 62%, transparent)`;
    }
    if (theme.radius !== undefined) {
      vars['--tenant-radius'] = `${theme.radius}rem`;
      vars['--tenant-radius-box'] = `${round(theme.radius * 0.8)}rem`;
      vars['--tenant-radius-field'] = `${round(theme.radius * 0.4)}rem`;
      vars['--tenant-radius-selector'] = `${round(theme.radius * 0.8)}rem`;
    }
    return vars;
  };
  return { light: build('light'), dark: build('dark') };
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
